import format from 'string-template';

import { TagCategory } from '../../../../Model/ExerciseTag';
import {
  UploadStatus,
  SaveStatus,
  CardStatus,
  MAX_FILE_SIZE_BYTES,
  MAX_FILE_SIZE_MB,
} from './constants';
import texts from './texts';

let cardCounter = 0;

const nextCardId = () => {
  cardCounter += 1;
  return `bulk-card-${cardCounter}`;
};

// Fallback display name for a card whose name is empty.
const defaultCardName = (number) => format(texts.defaultCardName, { number });

/**
 * Initial upload state for a card, derived from its file and any local
 * validation error.
 */
const buildUpload = (file, uploadError) => {
  if (uploadError) {
    // Local validation failure (e.g. oversize) — never entered the queue, so
    // there is no failedStep and nothing to retry; only re-select or remove.
    return {
      status: UploadStatus.FAILED, progress: 0, etaSeconds: null, error: uploadError, videoId: null, failedStep: null,
    };
  }
  if (file) {
    // Uploads start automatically: cards enter the queue on creation.
    return {
      status: UploadStatus.QUEUED, progress: 0, etaSeconds: null, error: null, videoId: null, failedStep: null,
    };
  }
  return {
    status: UploadStatus.NONE, progress: 0, etaSeconds: null, error: null, videoId: null, failedStep: null,
  };
};

const createCard = ({
  file = null, name = '', uploadError = null,
} = {}) => ({
  id: nextCardId(),
  name,
  description: '',
  tags: [],
  file,
  fileName: file ? file.name : '', // survives persistence — `file` itself cannot
  previewUrl: file ? URL.createObjectURL(file) : null,
  remoteVideoUrl: null, // embeddable player URL from the created exercise doc (session restore)
  upload: buildUpload(file, uploadError),
  save: SaveStatus.IDLE,
  saveError: null,
  exerciseId: null, // set after Exercise.addDoc — retained so retries never re-create the doc
  enqueued: false, // video processing enqueued (last save step)
  collapsed: false,
  justEntered: true,
});

/**
 * Serializable snapshot of a card for session recovery. The File object,
 * preview URL, and live progress are deliberately excluded — none survive a
 * reload — so this stays stable across upload-progress ticks.
 */
const serializeCard = (card) => ({
  name: card.name,
  description: card.description,
  tags: card.tags,
  fileName: card.fileName,
  uploadStatus: card.upload ? card.upload.status : UploadStatus.NONE,
  videoId: (card.upload && card.upload.videoId) || null,
  save: card.save,
  exerciseId: card.exerciseId,
  enqueued: card.enqueued,
});

/**
 * Rebuilds a card from a persisted snapshot after a reload:
 * - saved cards come back as collapsed done-rows;
 * - uploaded-but-unsaved cards keep their videoId (the bytes are on Vimeo)
 *   and are immediately saveable — no file needed;
 * - interrupted/failed uploads lost their File with the page, so they come
 *   back in a failed "re-select the file" state (re-dropping a file with the
 *   same name re-attaches it — see useCardFiles).
 */
const restoreCard = (persisted) => {
  const isSaved = persisted.save === SaveStatus.SAVED;
  let upload;
  if (persisted.uploadStatus === UploadStatus.UPLOADED && persisted.videoId) {
    upload = {
      status: UploadStatus.UPLOADED,
      progress: 100,
      etaSeconds: null,
      error: null,
      videoId: persisted.videoId,
      failedStep: null,
    };
  } else if (persisted.uploadStatus === UploadStatus.NONE) {
    upload = buildUpload(null);
  } else {
    // failedStep stays null: there is nothing to resume without the file, so
    // the card offers a file picker instead of "Retry upload". fileLost swaps
    // the picker label to "Choose a file" — there is no previous file to be
    // "different" from after a reload.
    upload = { ...buildUpload(null, texts.fileLostError), fileLost: true };
  }
  return {
    id: nextCardId(), // fresh id — the counter reset with the page
    name: persisted.name || '',
    description: persisted.description || '',
    tags: persisted.tags || [],
    file: null,
    fileName: persisted.fileName || '',
    previewUrl: null,
    remoteVideoUrl: null, // enriched from the exercise doc after restore
    upload,
    // An interrupted SAVING/SAVE_FAILED card returns as a saveable draft; its
    // exerciseId (if the doc was created) is retained so retry never duplicates.
    save: isSaved ? SaveStatus.SAVED : SaveStatus.IDLE,
    saveError: null,
    exerciseId: persisted.exerciseId || null,
    enqueued: !!persisted.enqueued,
    collapsed: isSaved,
    justEntered: false,
  };
};

// Compact "time remaining" label for an upload ETA, e.g. "45s" or "2m 10s".
// Clips are capped at 50MB so hours never realistically occur.
const formatEta = (seconds) => {
  if (seconds < 60) {
    return `${Math.max(1, Math.round(seconds))}s`;
  }
  const mins = Math.floor(seconds / 60);
  const secs = Math.round(seconds % 60);
  return secs ? `${mins}m ${secs}s` : `${mins}m`;
};

const isVideoFile = (file) => !!file && (file.type ? file.type.startsWith('video/') : true);

// An oversized file still becomes a card, but in a failed state with a clear
// per-file message + "Choose different file" — so the coach sees exactly which
// files are too large instead of a vague batch toast. Returns null when valid.
const oversizeError = (file) => (file && file.size > MAX_FILE_SIZE_BYTES
  ? format(texts.oversizeError, { max: MAX_FILE_SIZE_MB })
  : null);

// Maps a card's selected tags back to the (id, tag) shape the single-exercise
// editor persists, dropping platform tags.
const toPersistedTags = (cardTags) => {
  const persistedTags = cardTags
    .filter(({ category }) => category !== TagCategory.PLATFORM)
    .map(({ id, label, category }) => ({ id, tag: label, category }));
  return { persistedTags, persistedTagIds: persistedTags.map(({ id }) => id) };
};

const isUploading = (card) => card.upload && card.upload.status === UploadStatus.UPLOADING;
const isQueued = (card) => card.upload && card.upload.status === UploadStatus.QUEUED;
const isUploadFailed = (card) => card.upload && card.upload.status === UploadStatus.FAILED;

// Video requirement satisfied: uploaded, or intentionally has no video.
const isVideoSettled = (card) => (
  !card.upload
  || card.upload.status === UploadStatus.UPLOADED
  || card.upload.status === UploadStatus.NONE
);

/**
 * Duplicate-name detection using only client-side data:
 * existing library exercises (already loaded in ExerciseContext) + names within this batch.
 * A saved card's own library entry is excluded — it must not collide with itself.
 */
const getNameError = (card, index, cards, existingExercises = []) => {
  const name = card.name.trim();
  if (!name) {
    return ''; // "needs a name" is a soft state, not a hard error
  }
  const lower = name.toLowerCase();
  const collides = existingExercises.some((existing) => (
    existing.id !== card.exerciseId && existing.name.trim().toLowerCase() === lower
  ));
  if (collides) {
    return format(texts.nameExistsError, { name });
  }
  const duplicateIndex = cards.findIndex((other, otherIndex) => (
    otherIndex !== index && other.name.trim().toLowerCase() === lower
  ));
  if (duplicateIndex > -1 && duplicateIndex < index) {
    return texts.duplicateNameError;
  }
  return '';
};

// Field validity for a precomputed nameError — the single validation rule
// shared by the status machine and the save flow.
const isCardValid = (card, nameError) => !!card.name.trim() && isVideoSettled(card) && !nameError;

// Field validity independent of the status machine — used for fresh saves,
// save-failed retries, and auto-save alike.
const hasValidFields = (card, index, cards, existingExercises = []) => (
  isCardValid(card, getNameError(card, index, cards, existingExercises))
);

/**
 * Single derived status for a card, in priority order (matches the design).
 */
const getCardStatus = (card, nameError) => {
  if (card.save === SaveStatus.SAVED) {
    return CardStatus.SAVED;
  }
  if (card.save === SaveStatus.SAVING) {
    return CardStatus.SAVING;
  }
  if (card.save === SaveStatus.SAVE_FAILED) {
    return CardStatus.SAVE_FAILED;
  }
  if (isUploading(card)) {
    return CardStatus.UPLOADING;
  }
  if (isQueued(card)) {
    return CardStatus.QUEUED;
  }
  if (isUploadFailed(card)) {
    return CardStatus.UPLOAD_FAILED;
  }
  if (card.save === SaveStatus.IDLE && isCardValid(card, nameError)) {
    return CardStatus.READY;
  }
  return CardStatus.NEED;
};

const getNeedReason = (card, nameError) => {
  if (!card.name.trim()) {
    return texts.needName;
  }
  if (nameError) {
    return texts.fixName;
  }
  return texts.incomplete;
};

/**
 * Single derived view-model for a card: status + validation, computed once (in
 * the parent, memoized) so the card component doesn't recompute it across the
 * status badge, the Save button, and the footer summary.
 */
const getCardView = (card, index, cards, existingExercises = []) => {
  const nameError = getNameError(card, index, cards, existingExercises);
  const status = getCardStatus(card, nameError);
  return {
    status,
    nameError,
    ready: status === CardStatus.READY,
    needReason: status === CardStatus.NEED ? getNeedReason(card, nameError) : '',
  };
};

/**
 * Aggregate counts for the fixed footer summary, derived from the per-card views.
 */
const summarize = (views) => {
  const counts = {
    total: views.length,
    saved: 0,
    ready: 0,
    uploading: 0,
    needs: 0,
    uploadFailed: 0,
    saveFailed: 0,
  };
  views.forEach(({ status }) => {
    switch (status) {
      case CardStatus.SAVED:
        counts.saved += 1;
        break;
      case CardStatus.READY:
        counts.ready += 1;
        break;
      // Queued cards are upload work in flight — surface them as "uploading".
      case CardStatus.QUEUED:
      case CardStatus.UPLOADING:
        counts.uploading += 1;
        break;
      case CardStatus.NEED:
        counts.needs += 1;
        break;
      case CardStatus.UPLOAD_FAILED:
        counts.uploadFailed += 1;
        break;
      case CardStatus.SAVE_FAILED:
        counts.saveFailed += 1;
        break;
      default:
        break;
    }
  });
  return { ...counts, unsaved: counts.total - counts.saved };
};

// Saved cards sort to the top; otherwise order is stable.
const orderCards = (cards) => cards
  .map((card, index) => ({ card, index }))
  .sort((a, b) => (b.card.save === SaveStatus.SAVED ? 1 : 0) - (a.card.save === SaveStatus.SAVED ? 1 : 0));

export {
  createCard,
  serializeCard,
  restoreCard,
  buildUpload,
  defaultCardName,
  formatEta,
  isVideoFile,
  oversizeError,
  toPersistedTags,
  hasValidFields,
  getCardView,
  summarize,
  orderCards,
};
